import { useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, ReferenceLine, ReferenceDot, Tooltip } from "recharts";

/**
 * ElevationProfile
 * Props:
 *  - gpxPoints: [[lat, lng, ele?], ...]
 *  - series: [{ t, ele }, ...] optional precomputed time/elevation points
 *  - sectionLines: [seconds, ...] section boundary times to show as vertical lines
 *  - duration: video duration in seconds
 *  - currentTime: seconds
 *  - onSeek(seconds)
 *  - onHoverIndex(index|null)
 *  - onHoverTime(seconds|null)
 *  - highlightTime(seconds|null)
 */
export default function ElevationProfile({
  gpxPoints = [],
  series = null,
  sectionLines = [],
  duration = 0,
  currentTime = 0,
  onSeek,
  onHoverIndex,
  onHoverTime,
  highlightTime = null,
}) {
  const data = useMemo(() => {
    if (Array.isArray(series) && series.length > 0) {
      return series
        .map((p, i) => {
          if (p == null || p.ele == null || p.t == null) return null;
          return { t: Number(p.t), ele: p.ele, idx: i };
        })
        .filter(Boolean);
    }

    return gpxPoints
      .map((p, i) => {
        if (p.length < 3) return null;
        const t =
          duration > 0 ? (i / Math.max(1, gpxPoints.length - 1)) * duration : i;
        return { t, ele: p[2], idx: i };
      })
      .filter(Boolean);
  }, [gpxPoints, duration, series]);

  const highlightedPoint = useMemo(() => {
    const targetTime = Number(highlightTime);
    if (!Number.isFinite(targetTime) || data.length === 0) return null;

    let nearest = data[0];
    let minDelta = Math.abs(targetTime - Number(data[0].t));
    for (let i = 1; i < data.length; i++) {
      const d = Math.abs(targetTime - Number(data[i].t));
      if (d < minDelta) {
        minDelta = d;
        nearest = data[i];
      }
    }
    return nearest && Number.isFinite(Number(nearest.t)) && nearest.ele != null
      ? nearest
      : null;
  }, [data, highlightTime]);

  if (data.length === 0) return null;

  const handleClick = (e) => {
    if (!e || !e.activeLabel || !onSeek) return;
    onSeek(Number(e.activeLabel));
  };

  const handleMouseMove = (e) => {
    const payload = e?.activePayload?.[0]?.payload;
    const payloadIdx = payload?.idx;
    const payloadTime = payload?.t;

    if (onHoverIndex) {
      if (Number.isInteger(payloadIdx)) {
        onHoverIndex(payloadIdx);
      } else {
        const activeT = Number(e?.activeLabel);
        if (!Number.isFinite(activeT) || data.length === 0) {
          onHoverIndex(null);
        } else {
          let nearest = data[0];
          let minDelta = Math.abs(activeT - Number(data[0].t));
          for (let i = 1; i < data.length; i++) {
            const d = Math.abs(activeT - Number(data[i].t));
            if (d < minDelta) {
              minDelta = d;
              nearest = data[i];
            }
          }
          onHoverIndex(Number.isInteger(nearest?.idx) ? nearest.idx : null);
        }
      }
    }

    if (onHoverTime) {
      if (Number.isFinite(Number(payloadTime))) {
        onHoverTime(Number(payloadTime));
        return;
      }

      const activeT = Number(e?.activeLabel);
      if (!Number.isFinite(activeT) || data.length === 0) {
        onHoverTime(null);
        return;
      }

      let nearest = data[0];
      let minDelta = Math.abs(activeT - Number(data[0].t));
      for (let i = 1; i < data.length; i++) {
        const d = Math.abs(activeT - Number(data[i].t));
        if (d < minDelta) {
          minDelta = d;
          nearest = data[i];
        }
      }
      onHoverTime(Number.isFinite(Number(nearest?.t)) ? Number(nearest.t) : null);
    }
  };

  const handleMouseLeave = () => {
    if (onHoverIndex) onHoverIndex(null);
    if (onHoverTime) onHoverTime(null);
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="h-32 w-full px-0 pb-2 bg-[#1A1D1A] border-t border-white/10" data-testid="elevation-profile">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#C86B4E] pt-2 px-3">
        Elevation profile
      </div>
      <ResponsiveContainer width="100%" height="82%">
        <AreaChart
          data={data}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
        >
          <defs>
            <linearGradient id="eleFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#C86B4E" stopOpacity={0.7} />
              <stop offset="100%" stopColor="#C86B4E" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t" type="number" domain={["dataMin", "dataMax"]} hide />
          <YAxis dataKey="ele" type="number" domain={["auto", "auto"]} hide />
          <Tooltip
            cursor={{ stroke: "#C86B4E", strokeWidth: 1 }}
            contentStyle={{ background: "#1A1D1A", border: "1px solid #C86B4E", borderRadius: 8, fontSize: 11 }}
            labelFormatter={(v) => `t = ${fmt(v)}`}
            formatter={(val) => [`${Math.round(val)} m`, "elev"]}
          />
          <Area
            type="monotone"
            dataKey="ele"
            stroke="#C86B4E"
            strokeWidth={2}
            fill="url(#eleFill)"
            isAnimationActive={false}
          />
          {sectionLines.map((x) => (
            <ReferenceLine
              key={`section-${x}`}
              x={x}
              stroke="#C86B4E"
              strokeOpacity={0.45}
              strokeWidth={1}
              strokeDasharray="2 3"
            />
          ))}
          {currentTime > 0 && (
            <ReferenceLine x={currentTime} stroke="#fff" strokeWidth={2} strokeDasharray="4 3" />
          )}
          {highlightedPoint && (
            <ReferenceDot
              x={highlightedPoint.t}
              y={highlightedPoint.ele}
              r={5}
              fill="#2C5545"
              stroke="#fff"
              strokeWidth={2}
              isFront
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
